import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Eye, EyeOff, Heart, Shield, Users, Guitar as Hospital, AlertCircle, CheckCircle, Loader2, Info } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

const LoginPage: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isSignUp, setIsSignUp] = useState(false);
  const [selectedRole, setSelectedRole] = useState<'donor' | 'recipient' | 'hospital'>('donor');
  const [fullName, setFullName] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const navigate = useNavigate();
  const { signIn, signUp } = useAuth();

  const roleOptions = [
    { value: 'donor' as const, label: 'Organ Donor', icon: Heart, description: 'Individual donor registration' },
    { value: 'recipient' as const, label: 'Recipient', icon: Users, description: 'Patient awaiting transplant' },
    { value: 'hospital' as const, label: 'Hospital Staff', icon: Hospital, description: 'Medical professional' },
  ];

  const resetForm = () => {
    setEmail('');
    setPassword('');
    setFullName('');
    setError('');
    setSuccess('');
  };

  const validateForm = () => {
    // Clear previous errors
    setError('');

    // Check required fields
    if (!email?.trim()) {
      setError('Email is required');
      return false;
    }

    if (!password) {
      setError('Password is required');
      return false;
    }

    if (isSignUp && !fullName?.trim()) {
      setError('Full name is required');
      return false;
    }

    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email.trim())) {
      setError('Please enter a valid email address');
      return false;
    }

    // Password validation
    if (password.length < 6) {
      setError('Password must be at least 6 characters long');
      return false;
    }

    // Full name validation for signup
    if (isSignUp && fullName.trim().length < 2) {
      setError('Please enter your full name (at least 2 characters)');
      return false;
    }

    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      if (isSignUp) {
        console.log('Attempting to sign up:', email.trim(), selectedRole);
        
        const { data, error } = await signUp(email.trim(), password, {
          full_name: fullName.trim(),
          role: selectedRole,
        });
        
        if (error) {
          throw error;
        }
        
        if (data?.user) {
          if (!data.user.email_confirmed_at) {
            setSuccess('Account created successfully! You can now sign in with your credentials.');
            setIsSignUp(false);
            resetForm();
          } else {
            // User is automatically signed in
            setSuccess('Account created and signed in successfully!');
            setTimeout(() => {
              navigate('/');
            }, 1500);
          }
        } else {
          setSuccess('Account created successfully! You can now sign in with your credentials.');
          setIsSignUp(false);
          resetForm();
        }
      } else {
        console.log('Attempting to sign in:', email.trim());
        
        const { data, error } = await signIn(email.trim(), password);
        
        if (error) {
          throw error;
        }
        
        if (data?.user) {
          setSuccess('Signed in successfully!');
          setTimeout(() => {
            navigate('/');
          }, 1500);
        }
      }
    } catch (error: any) {
      console.error('Auth error:', error);
      
      // Handle specific error cases with user-friendly messages
      let errorMessage = 'An unexpected error occurred. Please try again.';
      
      if (error.message) {
        if (error.message.includes('Invalid login credentials') || error.message.includes('Invalid credentials')) {
          errorMessage = 'Invalid email or password. Please check your credentials and try again.';
        } else if (error.message.includes('Email not confirmed')) {
          errorMessage = 'Please check your email and click the confirmation link before signing in.';
        } else if (error.message.includes('User already registered') || error.message.includes('already exists')) {
          errorMessage = 'An account with this email already exists. Please sign in instead.';
          setIsSignUp(false);
        } else if (error.message.includes('Demo mode')) {
          errorMessage = 'This is a demo version. Authentication is disabled. Please set up Supabase credentials to enable full functionality.';
        } else if (error.message.includes('signup is disabled')) {
          errorMessage = 'New user registration is currently disabled. Please contact support.';
        } else if (error.message.includes('Invalid email')) {
          errorMessage = 'Please enter a valid email address.';
        } else if (error.message.includes('Password should be at least')) {
          errorMessage = 'Password must be at least 6 characters long.';
        } else if (error.message.includes('rate limit') || error.message.includes('Too many')) {
          errorMessage = 'Too many attempts. Please wait a moment before trying again.';
        } else if (error.message.includes('Network error') || error.message.includes('fetch')) {
          errorMessage = 'Network error. Please check your internet connection and try again.';
        } else if (error.message.includes('User not found')) {
          errorMessage = 'No account found with this email address. Please sign up first.';
        } else {
          errorMessage = error.message;
        }
      }
      
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const switchMode = () => {
    setIsSignUp(!isSignUp);
    resetForm();
  };

  // Check if Supabase is properly configured
  const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
  const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;
  const isDemoMode = !supabaseUrl || !supabaseAnonKey ||
                     supabaseUrl === 'https://demo-supabase-url.supabase.co' ||
                     supabaseAnonKey === 'demo-anon-key-placeholder';

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div className="text-center">
          <div className="flex justify-center mb-6">
            <div className="relative">
              <div className="p-4 bg-gradient-to-br from-blue-600 to-blue-700 rounded-2xl shadow-xl">
                <Heart className="h-8 w-8 text-white" />
              </div>
              <div className="absolute -top-1 -right-1 w-4 h-4 bg-green-500 rounded-full animate-pulse"></div>
            </div>
          </div>
          <h2 className="text-3xl font-bold text-gray-900">
            {isSignUp ? 'Create your account' : 'Welcome back'}
          </h2>
          <p className="mt-2 text-sm text-gray-600">
            {isSignUp ? (
              <>
                Already have an account?{' '}
                <button
                  onClick={switchMode}
                  className="font-medium text-blue-600 hover:text-blue-500 transition-colors"
                  disabled={loading}
                >
                  Sign in
                </button>
              </>
            ) : (
              <>
                Don't have an account?{' '}
                <button
                  onClick={switchMode}
                  className="font-medium text-blue-600 hover:text-blue-500 transition-colors"
                  disabled={loading}
                >
                  Sign up
                </button>
              </>
            )}
          </p>
        </div>

        <div className="bg-white py-8 px-6 shadow-xl rounded-2xl border border-gray-200 space-y-6">
          {/* Demo Mode Notice */}
          {isDemoMode && (
            <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
              <div className="flex items-start space-x-3">
                <Info className="h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0" />
                <div>
                  <p className="text-sm font-medium text-blue-900">Demo Mode Active</p>
                  <p className="text-xs text-blue-700 mt-1">
                    Authentication is disabled. Set up Supabase credentials to enable full functionality.
                  </p>
                </div>
              </div>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-6">
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-xl text-sm flex items-center space-x-3 animate-fade-in">
                <AlertCircle className="h-5 w-5 flex-shrink-0" />
                <span>{error}</span>
              </div>
            )}

            {success && (
              <div className="bg-green-50 border border-green-200 text-green-600 px-4 py-3 rounded-xl text-sm flex items-center space-x-3 animate-fade-in">
                <CheckCircle className="h-5 w-5 flex-shrink-0" />
                <span>{success}</span>
              </div>
            )}

            {isSignUp && (
              <>
                <div>
                  <label htmlFor="fullName" className="block text-sm font-bold text-gray-700 mb-2">
                    Full Name *
                  </label>
                  <input
                    id="fullName"
                    name="fullName"
                    type="text"
                    required
                    value={fullName}
                    onChange={(e) => setFullName(e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300"
                    placeholder="Enter your full name"
                    disabled={loading}
                    minLength={2}
                  />
                </div>

                {/* Role Selection */}
                <div className="space-y-3">
                  <label className="text-sm font-bold text-gray-700">Account Type *</label>
                  <div className="grid grid-cols-1 gap-2">
                    {roleOptions.map((role) => {
                      const Icon = role.icon;
                      return (
                        <label
                          key={role.value}
                          className={`flex items-center p-3 border rounded-xl cursor-pointer transition-all duration-300 hover:shadow-md ${
                            selectedRole === role.value
                              ? 'border-blue-500 bg-blue-50 shadow-md'
                              : 'border-gray-200 hover:border-gray-300'
                          } ${loading ? 'opacity-50 cursor-not-allowed' : ''}`}
                        >
                          <input
                            type="radio"
                            name="role"
                            value={role.value}
                            checked={selectedRole === role.value}
                            onChange={(e) => setSelectedRole(e.target.value as any)}
                            className="sr-only"
                            disabled={loading}
                          />
                          <Icon className={`h-5 w-5 mr-3 ${
                            selectedRole === role.value ? 'text-blue-600' : 'text-gray-400'
                          }`} />
                          <div className="flex-1">
                            <div className="text-sm font-bold text-gray-900">{role.label}</div>
                            <div className="text-xs text-gray-500">{role.description}</div>
                          </div>
                        </label>
                      );
                    })}
                  </div>
                </div>
              </>
            )}

            <div>
              <label htmlFor="email" className="block text-sm font-bold text-gray-700 mb-2">
                Email address *
              </label>
              <input
                id="email"
                name="email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300"
                placeholder="Enter your email"
                disabled={loading}
              />
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-bold text-gray-700 mb-2">
                Password *
              </label>
              <div className="relative">
                <input
                  id="password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full px-4 py-3 pr-12 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300"
                  placeholder="Enter your password"
                  minLength={6}
                  disabled={loading}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute inset-y-0 right-0 pr-4 flex items-center hover:text-blue-600 transition-colors"
                  disabled={loading}
                >
                  {showPassword ? (
                    <EyeOff className="h-5 w-5 text-gray-400" />
                  ) : (
                    <Eye className="h-5 w-5 text-gray-400" />
                  )}
                </button>
              </div>
              {isSignUp && (
                <p className="mt-2 text-xs text-gray-500">
                  Password must be at least 6 characters long
                </p>
              )}
            </div>

            {!isSignUp && (
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <input
                    id="remember-me"
                    name="remember-me"
                    type="checkbox"
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    disabled={loading}
                  />
                  <label htmlFor="remember-me" className="ml-2 block text-sm text-gray-900">
                    Remember me
                  </label>
                </div>

                <div className="text-sm">
                  <a href="#" className="font-medium text-blue-600 hover:text-blue-500 transition-colors">
                    Forgot password?
                  </a>
                </div>
              </div>
            )}

            <button
              type="submit"
              disabled={loading || isDemoMode}
              className="w-full flex justify-center items-center py-3 px-4 border border-transparent rounded-xl shadow-lg text-sm font-bold text-white bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300 hover:scale-105"
            >
              {loading ? (
                <div className="flex items-center space-x-2">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  <span>{isSignUp ? 'Creating account...' : 'Signing in...'}</span>
                </div>
              ) : (
                isSignUp ? 'Create Account' : 'Sign in'
              )}
            </button>
          </form>

          <div className="flex items-center justify-center space-x-2 text-xs text-gray-500 pt-4 border-t border-gray-200">
            <Shield className="h-4 w-4" />
            <span>HIPAA Compliant • Your data is protected</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;